import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../auth/AuthProvider';
import { useTranslation } from '../i18n';
import { PageHeader } from '../components/Section';

type Step = 'idle' | 'codeSent';

export default function RecoveryEmail() {
  const t = useTranslation();
  const { user, refresh } = useAuth();

  const [step, setStep] = useState<Step>('idle');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep('idle');
    setCurrentPassword('');
    setNewEmail('');
    setCode('');
    setError(null);
    setInfo(null);
  };

  const sendCode = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/v1/account/email/start', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newEmail }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(data.error || t.recoveryEmail.errors.generic);
        return;
      }
      setStep('codeSent');
      setInfo(t.recoveryEmail.codeSent);
    } catch {
      setError(t.recoveryEmail.errors.generic);
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/v1/account/email/verify', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newEmail, code }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(data.error || t.recoveryEmail.errors.generic);
        return;
      }
      await refresh();
      setInfo(t.recoveryEmail.updated);
      setStep('idle');
      setCurrentPassword('');
      setNewEmail('');
      setCode('');
    } catch {
      setError(t.recoveryEmail.errors.generic);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader title={t.recoveryEmail.title} description={t.recoveryEmail.description} />
      <Paper sx={{ p: 3, maxWidth: 480 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t.recoveryEmail.currentLabel}
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {user?.email || t.recoveryEmail.notSet}
            </Typography>
          </Box>

          {info && <Alert severity="success">{info}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          {step === 'idle' && (
            <Box component="form" onSubmit={sendCode}>
              <Stack spacing={2}>
                <TextField
                  type="password"
                  label={t.recoveryEmail.currentPassword}
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
                <TextField
                  type="email"
                  label={t.recoveryEmail.newEmail}
                  autoComplete="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                />
                <Box>
                  <Button type="submit" variant="contained" disabled={submitting}>
                    {submitting ? t.common.loading : t.recoveryEmail.sendCode}
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}

          {step === 'codeSent' && (
            <Box component="form" onSubmit={verifyCode}>
              <Stack spacing={2}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t.recoveryEmail.codeInstructions.replace('{email}', newEmail)}
                </Typography>
                <TextField
                  label={t.recoveryEmail.code}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
                />
                <Stack direction="row" spacing={1}>
                  <Button type="submit" variant="contained" disabled={submitting}>
                    {submitting ? t.common.loading : t.recoveryEmail.verify}
                  </Button>
                  <Button onClick={reset} disabled={submitting}>
                    {t.common.cancel}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
